/**
 * TicketEvidenceWorkflow — the bounded technician path from ticket to verified outcome
 * (PROD-UX-070 / FULL-0156, MASTER_FLOW §14C.1 / §14C.3 / §14C.5).
 *
 * It renders the four artifacts the roadmap requires to stay distinct, in order:
 *
 *   ticket (information) → AI recommendation (recommendation) → proposed action (approval
 *   required) → verified outcome (verified, or an explicit not-verified/warning/unknown arm).
 *
 * The derivation lives in `ticketEvidenceWorkflowModel.js`; this file only presents it.
 *
 * ACCESSIBILITY / INTERACTION
 *   - The rail is an ordered list of four focusable steps with a roving tabindex. In this RTL
 *     surface ArrowLeft/ArrowDown move to the next step, ArrowRight/ArrowUp to the previous one,
 *     and Home/End jump to the ends. `aria-current="step"` names the operator's current step.
 *   - Recovery and next actions are native buttons/links. A retry button works from the keyboard;
 *     stale, offline, loading and denied states each render an explicit reason.
 *   - Approving is NOT done here. The workflow's approval action only moves focus to the existing
 *     human review card below; `agentRunsApi` and the server-side approval gate stay authoritative.
 *
 * SOURCE HONESTY
 *   The run source is classified once (loading/fresh/stale/failed) and every step carries
 *   `data-stage-freshness`. A failed refresh does not hide the last payload; it labels it stale and
 *   disables start/approve. A page with no payload labels the run-derived steps as unavailable and
 *   never renders an empty state as if it were a result.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CriticalStateBadge from "./designSystem/CriticalStateBadge";
import { useOnlineStatus } from "./useLiveState";
import {
  WORKFLOW_AUTHORITY,
  classifyRunsSource,
  deriveTicketEvidenceWorkflow,
} from "./ticketEvidenceWorkflowModel";
import { SOURCE_STATES } from "./statusVocabulary";

const KEYBOARD_HINT_ID = "ticket-evidence-workflow-keyboard-hint";

const SOURCE_COPY = {
  [SOURCE_STATES.loading]: "מקור הנתונים: נתוני הריצות נטענים.",
  [SOURCE_STATES.fresh]: "מקור הנתונים: נתוני הריצות נטענו.",
  [SOURCE_STATES.stale]: "מקור הנתונים: הטעינה האחרונה; הרענון נכשל.",
  [SOURCE_STATES.failed]: "מקור הנתונים: נתוני הריצות אינם זמינים.",
};

// No `focus:outline-none` here: the visible focus ring is owned by `.prodigy-workflow-stage:focus-visible`
// in designTokens.css, and a Tailwind focus utility would suppress it depending on stylesheet order.
const STAGE_CLASS = "prodigy-workflow-stage rounded-xl border bg-white p-3 space-y-2 min-w-0";

function RetryButton({ onRetry, online, testId }) {
  if (typeof onRetry !== "function") return null;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onRetry}
      disabled={!online}
      title={online ? undefined : "אין חיבור לרשת — לא ניתן לרענן עד לחזרת החיבור."}
      className="text-xs border border-gray-300 rounded-lg px-2.5 py-1 hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
    >
      נסה שוב
    </button>
  );
}

export default function TicketEvidenceWorkflow({
  ticket = null,
  technicianName = null,
  runs = [],
  runsLoading = false,
  runsFailed = false,
  runsUpdatedAt = 0,
  canRun = false,
  canApprove = false,
  onStartAnalysis,
  onRetry,
  onFocusApproval,
}) {
  const online = useOnlineStatus();
  const source = useMemo(
    () =>
      classifyRunsSource({
        loading: runsLoading,
        failed: runsFailed,
        hasData: (Array.isArray(runs) && runs.length > 0) || runsUpdatedAt > 0,
        updatedAt: runsUpdatedAt,
      }),
    [runsLoading, runsFailed, runs, runsUpdatedAt]
  );
  const model = useMemo(
    () =>
      deriveTicketEvidenceWorkflow({
        ticket,
        technicianName,
        runs,
        source,
        online,
        canRun,
        canApprove,
      }),
    [ticket, technicianName, runs, source, online, canRun, canApprove]
  );

  const currentIndex = Math.max(
    0,
    model.stages.findIndex((stage) => stage.key === model.currentStageKey)
  );
  const [focusIndex, setFocusIndex] = useState(currentIndex);
  const stepRefs = useRef([]);

  useEffect(() => {
    setFocusIndex(currentIndex);
  }, [currentIndex]);

  if (!ticket) return null;

  const moveFocus = (event, index) => {
    let next = null;
    // RTL: the next step lies to the LEFT in the reading order. Vertical arrows follow the same
    // order so the rail also works as a list on narrow screens.
    if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      next = Math.min(index + 1, model.stages.length - 1);
    } else if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      next = Math.max(index - 1, 0);
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = model.stages.length - 1;
    }
    if (next == null) return;
    event.preventDefault();
    setFocusIndex(next);
    stepRefs.current[next]?.focus();
  };

  const renderAction = (stage) => {
    const action = stage.action;
    if (!action) return null;

    if (action.kind === "open_run") {
      return (
        <Link
          to={`/agent-runs/${action.runId}`}
          data-testid="workflow-open-run"
          className="inline-block text-xs text-blue-700 hover:underline"
        >
          {action.label}
        </Link>
      );
    }

    const needsFocusHandler = action.kind === "focus_approval" && typeof onFocusApproval !== "function";
    const disabled = action.disabled || needsFocusHandler;
    const disabledReason = needsFocusHandler
      ? "האישור מתבצע בכרטיס הפעולה למטה."
      : action.disabledReason;
    const onClick =
      action.kind === "start"
        ? () => onStartAnalysis?.()
        : () => onFocusApproval?.(action.runId);

    return (
      <div className="space-y-1">
        <button
          type="button"
          data-testid={`workflow-action-${stage.key}`}
          data-disabled-reason={disabled ? disabledReason || "לא זמין" : undefined}
          onClick={onClick}
          disabled={disabled}
          title={disabled ? disabledReason : undefined}
          className="bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {action.label}
        </button>
        {disabled && disabledReason && (
          <p
            data-testid={`workflow-action-reason-${stage.key}`}
            className="text-xs text-gray-500"
          >
            {disabledReason}
          </p>
        )}
      </div>
    );
  };

  return (
    <section
      dir="rtl"
      data-testid="ticket-evidence-workflow"
      data-presentation-only={WORKFLOW_AUTHORITY.presentationOnly ? "true" : undefined}
      data-workflow-source={source.key}
      aria-label="מסלול העבודה מהקריאה לתוצאה מאומתת"
      className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 xl:p-4 space-y-3"
    >
      <div>
        <h2 className="text-base font-bold text-gray-800">מסלול העבודה — מהקריאה לתוצאה מאומתת</h2>
        <p className="text-xs text-gray-500">
          ארבעה שלבים נפרדים: הקריאה, המלצת ה-AI, הפעולה המוצעת והתוצאה. כל שלב נשען על מקור אחר
          ואינו מחליף את השלב שאחריו.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <p
          data-testid="workflow-source"
          data-source-state={source.key}
          className="text-xs text-gray-500"
        >
          {SOURCE_COPY[source.key]}
        </p>
        {source.key === SOURCE_STATES.stale && (
          <RetryButton onRetry={onRetry} online={online} testId="workflow-retry-stale" />
        )}
      </div>

      <div aria-live="polite" className="space-y-1.5">
        {!online && (
          <p
            role="status"
            data-testid="workflow-offline"
            className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
          >
            אין חיבור לרשת — מסלול העבודה מוצג לקריאה בלבד; פעולות במסלול זה אינן זמינות עד
            לחזרת החיבור.
          </p>
        )}
        {source.key === SOURCE_STATES.loading && (
          <p
            role="status"
            data-testid="workflow-loading"
            className="text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded px-2 py-1"
          >
            טוען את נתוני מסלול העבודה...
          </p>
        )}
        {source.key === SOURCE_STATES.stale && (
          <p
            role="status"
            data-testid="workflow-stale"
            className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
          >
            רענון הנתונים נכשל — המוצג הוא מהטעינה האחרונה ואינו נוכחי; אין לאשר או להפעיל פעולה
            לפני רענון מוצלח.
          </p>
        )}
        {source.key === SOURCE_STATES.failed && (
          <div
            role="alert"
            data-testid="workflow-unavailable"
            className="flex items-center justify-between gap-2 flex-wrap text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1"
          >
            <span>לא ניתן לטעון את נתוני מסלול העבודה — אין להסיק מצב מנתונים חסרים.</span>
            <RetryButton onRetry={onRetry} online={online} testId="workflow-retry-inline" />
          </div>
        )}
      </div>

      <p id={KEYBOARD_HINT_ID} className="text-xs text-gray-400">
        ניווט בין השלבים: חצים, Home ו-End. הפעולות עצמן הן כפתורים או קישורים רגילים.
      </p>

      <ol
        aria-label="שלבי מסלול העבודה"
        aria-describedby={KEYBOARD_HINT_ID}
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 list-none p-0 m-0"
      >
        {model.stages.map((stage, index) => {
          const isCurrent = stage.key === model.currentStageKey;
          return (
            <li
              key={stage.key}
              ref={(node) => {
                stepRefs.current[index] = node;
              }}
              tabIndex={index === focusIndex ? 0 : -1}
              onFocus={() => setFocusIndex(index)}
              onKeyDown={(event) => moveFocus(event, index)}
              aria-current={isCurrent ? "step" : undefined}
              aria-label={`שלב ${stage.step} מתוך 4: ${stage.label} — ${stage.stateLabel}`}
              data-testid={`workflow-stage-${stage.key}`}
              data-workflow-stage={stage.key}
              data-workflow-state={stage.state}
              data-stage-step={stage.step}
              data-stage-freshness={source.key}
              data-current-step={isCurrent ? "true" : undefined}
              className={`${STAGE_CLASS}${isCurrent ? " ring-2 ring-brand-200" : ""}`}
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-[11px] font-bold text-gray-600 shrink-0"
                  >
                    {stage.step}
                  </span>
                  <h3 className="text-sm font-semibold text-gray-800">{stage.label}</h3>
                </div>
                <CriticalStateBadge
                  state={stage.state}
                  label={stage.stateLabel}
                  testId={`workflow-badge-${stage.key}`}
                />
              </div>

              <p className="text-xs text-gray-500">{stage.authority}</p>

              <ul className="text-xs text-gray-700 space-y-0.5">
                {stage.lines.map((line, lineIndex) => (
                  <li key={`${stage.key}-${lineIndex}`}>{line}</li>
                ))}
              </ul>

              {stage.basis && (
                <p
                  data-testid="workflow-verified-basis"
                  className="text-xs font-medium text-gray-600 border-t border-gray-100 pt-1.5"
                >
                  {stage.basis}
                </p>
              )}

              {stage.denied && (
                <div
                  data-testid={stage.denied.testId}
                  className="flex items-start gap-1.5 flex-wrap text-xs text-gray-600 border-t border-gray-100 pt-1.5"
                >
                  <CriticalStateBadge state="no_permission" label="אין הרשאה" />
                  <span>{stage.denied.reason}</span>
                </div>
              )}

              {renderAction(stage)}
            </li>
          );
        })}
      </ol>

      <p className="text-xs text-gray-400">
        המסלול מציג ומסביר בלבד. אישור הפעולה מתבצע בכרטיס הפעולה ובשרת, ולעולם לא כאן.
      </p>
    </section>
  );
}
